import { Link } from 'wouter';
import { LegalSection } from './LegalSection';
import { routes } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';

/**
 * Privacy Policy.
 *
 * A faithful migration of the legacy page at
 * `artifacts/high-performer-os/src/pages/privacy.tsx`. The substantive text is
 * copied verbatim and is not rewritten, summarized, or modernized here.
 *
 * V2-018C-2 completes the migration. The body prose landed earlier, before the
 * strict-parity directive, but three legacy behaviours had not: the page
 * metadata, the legacy content measure, and the closing link row. All three are
 * restored here.
 *
 * The measure is the legacy `<main>`'s own `max-w-3xl mx-auto px-4 py-16`. It
 * belongs on the page rather than the shell: the Experience Shell imposes no
 * width, because public pages are full-bleed bands that each set their own.
 * Before this slice the document ran edge to edge with no padding at all.
 *
 * Rendering adaptations only: the legacy page carried its own logo header and
 * `← Back` link, which the Experience Shell header now provides, and the
 * legacy `prose` classes are dropped because v2 has no typography plugin.
 *
 * Launch readiness — Slice A departs from legacy on points of fact only: the
 * processor list (see `PROCESSORS`), the authentication provider, the removal
 * of the daily check-in disclosures for a feature this product does not have,
 * the assessment coverage (three, not one), and the previously undisclosed
 * offer-impression record. Wording that requires a judgement call — the
 * effective date, contact addresses, retention commitments — is untouched here
 * and handled separately.
 */
const EFFECTIVE_DATE = 'June 11, 2026';

/**
 * The processors this application actually uses.
 *
 * The legacy list named Clerk for authentication and Neon for the database.
 * Neither is in this stack: authentication and the database are both Supabase
 * (ADR-0003), and the site and API are served from Cloudflare, which handles
 * the request logs the "Usage Data" section describes and was previously not
 * disclosed at all.
 */
const PROCESSORS = [
  {
    name: 'Supabase, Inc.',
    purpose:
      'User authentication, account creation, session management, and database hosting for assessment data, results, and account records.',
    link: 'https://supabase.com/privacy',
  },
  {
    name: 'Stripe, Inc.',
    purpose: 'Payment processing for the full report purchase.',
    link: 'https://stripe.com/privacy',
  },
  {
    name: 'Cloudflare, Inc.',
    purpose:
      'Website and API hosting, content delivery, and network-level request logging.',
    link: 'https://www.cloudflare.com/privacypolicy/',
  },
] as const;

export function PrivacyPolicy() {
  usePageMeta({
    title: 'Privacy Policy | High Performer OS',
    description:
      'Read the High Performer OS privacy policy to understand how your assessment data is collected, used, and protected.',
    canonical: 'https://highperformeros.com/privacy',
  });

  return (
    <article className="mx-auto max-w-3xl space-y-10 px-4 py-16">
      <div>
        <p className="mb-2 text-xs font-bold tracking-widest uppercase text-primary">
          Legal
        </p>
        <h1 className="mb-3 text-3xl font-bold">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="space-y-8">
        <LegalSection title="Overview">
          <p>
            High Performer OS ("we," "us," or "our") is committed to protecting
            your personal information. This Privacy Policy explains what data we
            collect, how we use it, who we share it with, and your rights
            regarding that data. By using our website and services, you agree to
            the practices described in this policy.
          </p>
        </LegalSection>

        <LegalSection title="Information We Collect">
          <h4 className="mb-2 text-sm font-semibold">Account Information</h4>
          <p>
            When you create an account, we collect your email address and the
            information required to authenticate your identity. Authentication
            is handled by Supabase, Inc. We do not store your password.
          </p>

          <h4 className="mt-4 mb-2 text-sm font-semibold">Assessment Data</h4>
          <p>
            When you complete the High Performer Bottleneck Assessment, the
            High Performer Work Fit Assessment, or the High Performer
            Perspective Assessment, we collect your 50 question responses
            (rated 1 to 5 on a Likert scale) and the resulting scores, profile
            or type classification, and dimension results for that assessment.
            This data is stored in our database and associated with your
            account.
          </p>

          <h4 className="mt-4 mb-2 text-sm font-semibold">
            Dashboard and Check-in Data
          </h4>
          <p>
            If you use the daily check-in feature, we collect your self-reported
            energy level, focus level, and optional notes. This data is stored
            and associated with your account.
          </p>

          <h4 className="mt-4 mb-2 text-sm font-semibold">
            Payment Information
          </h4>
          <p>
            When you purchase the full report, your payment is processed by
            Stripe, Inc. We do not store credit card numbers or full payment
            details. We retain a record of your purchase (amount, date, and
            Stripe session ID) associated with your account.
          </p>

          <h4 className="mt-4 mb-2 text-sm font-semibold">Usage Data</h4>
          <p>
            We may collect standard web server logs including your IP address,
            browser type, referring URL, and pages visited. This data is used
            for security and to improve the service.
          </p>
          <p>
            We also record when you view the locked full-report offer for one of
            your own results, so we can measure how often that offer is seen.
            This record is associated with your account and the result it
            relates to.
          </p>
        </LegalSection>

        <LegalSection title="How We Use Your Information">
          <ul className="list-inside list-disc space-y-1.5 text-sm">
            <li>
              To provide and display your assessment results and full report
            </li>
            <li>To authenticate your account and maintain your session</li>
            <li>To process your payment and verify purchase status</li>
            <li>To store your assessment history</li>
            <li>
              To improve the accuracy of assessment scoring and report content
            </li>
            <li>
              To send transactional emails related to your account (e.g.,
              purchase confirmation)
            </li>
            <li>To maintain the security and integrity of the service</li>
          </ul>
          <p className="mt-3">
            We do not sell your personal information to third parties. We do not
            use your assessment responses for advertising or marketing
            profiling.
          </p>
        </LegalSection>

        <LegalSection title="Third-Party Service Providers">
          <p>
            We use the following third-party processors to operate the service.
            Each is bound by their own privacy policies and data processing
            agreements.
          </p>
          <div className="mt-4 space-y-3">
            {PROCESSORS.map((processor) => (
              <div
                key={processor.name}
                className="rounded-lg border border-border bg-card p-4"
              >
                <p className="text-sm font-semibold">{processor.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {processor.purpose}
                </p>
                {'link' in processor && processor.link ? (
                  <a
                    href={processor.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {processor.link}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </LegalSection>

        <LegalSection title="Data Retention">
          <p>
            We retain your account data and assessment results for as long as
            your account is active or as needed to provide the service. You may
            request deletion of your data at any time by contacting us (see
            below). Upon a deletion request, we will remove your personal data
            within 30 days, except where retention is required by law.
          </p>
        </LegalSection>

        <LegalSection title="Data Security">
          <p>
            We implement reasonable technical and organizational measures to
            protect your data, including encrypted database connections, HTTPS
            for all data transmission, and access controls. However, no method
            of transmission over the internet or electronic storage is 100%
            secure. We cannot guarantee absolute security.
          </p>
        </LegalSection>

        <LegalSection title="Your Rights">
          <p>
            Depending on your location, you may have the following rights
            regarding your personal data:
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1.5 text-sm">
            <li>
              <strong>Access:</strong> Request a copy of the personal data we
              hold about you.
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate
              data.
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your account and
              associated data.
            </li>
            <li>
              <strong>Portability:</strong> Request your data in a
              machine-readable format.
            </li>
            <li>
              <strong>Objection:</strong> Object to processing of your data in
              certain circumstances.
            </li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, contact us at the address below.
          </p>
        </LegalSection>

        <LegalSection title="Children's Privacy">
          <p>
            High Performer OS is intended for adults aged 18 and older. We do
            not knowingly collect personal information from anyone under 18. If
            you believe we have inadvertently collected data from a minor,
            please contact us immediately.
          </p>
        </LegalSection>

        <LegalSection title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the effective date at the top. Continued use of the
            service after changes are posted constitutes acceptance of the
            updated policy. For material changes, we will make reasonable
            efforts to notify users directly.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p>
            If you have questions about this Privacy Policy or wish to exercise
            your data rights, please contact us at:
          </p>
          <div className="mt-3 rounded-lg border border-border bg-card p-4 text-sm">
            <p className="font-semibold">High Performer OS</p>
            <p className="mt-1 text-muted-foreground">
              Email: privacy@highperformeros.com
            </p>
          </div>
        </LegalSection>
      </div>

      {/* The legacy page's closing link row. It is not the site footer and is
          not a duplicate of it: legacy carries both, and this one links across
          to the sibling legal document and back to the homepage. */}
      <div
        data-testid="legal-page-links"
        className="flex gap-6 border-t border-border pt-8 text-xs text-muted-foreground"
      >
        <Link
          href={routes.terms}
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Terms of Service
        </Link>
        <Link
          href={routes.home}
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Home
        </Link>
      </div>
    </article>
  );
}
